import { useFormContext } from 'react-hook-form'
import { ReactNode, useCallback, useEffect, useState } from 'react'
import { FlexColumn, FlexRow } from '../../../atoms/layout/Flex'
import Label from '../../../atoms/display/Label'
import RadioControl from '../../../atoms/inputs/RadioControl'
import TextField from '../../../atoms/inputs/TextField'
import NumberControl from '../../../atoms/inputs/NumberControl'
import Divider from '../../../atoms/display/Divider'
import FileSelectButton from '../../../atoms/inputs/FileSelectButton'
import { VisitorRegistrationFormParams } from '../../../../types/Company'
import { AttachmentIcon } from '../../../atoms/display/Icons'
import IconButton from '../../../atoms/inputs/IconButton'
import FilePreviewDialog from './FilePreviewDialog'

function FormRow(props: { text: string; subText?: string; children: ReactNode }) {
  const { text, subText, children } = props
  return (
    <FlexColumn>
      <FlexRow align="center">
        <FlexColumn width={320} space={0}>
          <Label text={text} />
          {subText && <Label text={subText} variant="caption" />}
        </FlexColumn>
        {children}
      </FlexRow>
      <Divider />
    </FlexColumn>
  )
}

export default function VisitorRegistrationSurveysForm() {
  const { control, register, watch, setValue, trigger, formState } = useFormContext<VisitorRegistrationFormParams>()
  const { errors } = formState

  // プレビューダイアログの状態
  const [previewFile, setPreviewFile] = useState<{ id: string; name: string } | null>(null)

  // 加入状態とファイルを監視
  const survey4 = watch('survey4')
  const survey4File = watch('survey4File')
  const survey4FileId = watch('survey4FileId')
  const survey4FileName = watch('survey4FileName')
  const survey9 = watch('survey9')
  const survey9File = watch('survey9File')
  const survey9FileId = watch('survey9FileId')
  const survey9FileName = watch('survey9FileName')
  const survey16 = watch('survey16')
  const survey16File = watch('survey16File')
  const survey16FileId = watch('survey16FileId')
  const survey16FileName = watch('survey16FileName')

  // プレビューダイアログを開く
  const handleOpenPreview = useCallback((fileId: string, fileName: string) => {
    setPreviewFile({ id: fileId, name: fileName })
  }, [])

  // プレビューダイアログを閉じる
  const handleClosePreview = useCallback(() => {
    setPreviewFile(null)
  }, [])

  // 未加入選択時にファイルをクリア（MUIのRadioは値を文字列として扱うためNumber()で変換）
  useEffect(() => {
    if (Number(survey4) === 2) {
      setValue('survey4File', undefined)
    }
  }, [survey4, setValue])

  useEffect(() => {
    if (Number(survey9) === 2) {
      setValue('survey9File', undefined)
    }
  }, [survey9, setValue])

  useEffect(() => {
    if (Number(survey16) === 2) {
      setValue('survey16File', undefined)
    }
  }, [survey16, setValue])

  // ファイル選択ハンドラ
  const handleSelectSurvey4File = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setValue('survey4File', files[0])
        void trigger('survey4File')
      }
    },
    [setValue, trigger]
  )

  const handleSelectSurvey9File = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setValue('survey9File', files[0])
        void trigger('survey9File')
      }
    },
    [setValue, trigger]
  )

  const handleSelectSurvey16File = useCallback(
    (files: File[]) => {
      if (files.length > 0) {
        setValue('survey16File', files[0])
        void trigger('survey16File')
      }
    },
    [setValue, trigger]
  )

  return (
    <FlexColumn>
      <Label text="代表者がアンケートにお応えください" variant="subtitle" />
      <FormRow text="事業形態">
        <RadioControl
          items={[
            { value: 1, label: '法人' },
            { value: 2, label: '個人' },
          ]}
          control={control}
          name="survey1"
          row
          error={errors.survey1?.message}
        />
      </FormRow>
      <FormRow text="（個人事業主の方に質問）あなたは一人親方ですか？">
        <RadioControl
          items={[
            { value: 1, label: 'はい' },
            { value: 2, label: 'いいえ' },
          ]}
          control={control}
          name="survey17"
          row
          error={errors.survey17?.message}
        />
      </FormRow>
      <FormRow
        text="従業員は何人いますか？（雇用契約のある方のみ）"
        subText="※従業員がいる場合、「労働者一覧」をご提出ください"
      >
        <FlexRow wrap align="center">
          <Label text="従業員数" />
          <NumberControl control={control} name="survey18" suffix="名" size="xs" error={errors.survey18?.message} />
        </FlexRow>
      </FormRow>
      <FormRow
        text="（従業員のいる方に質問）労災保険に加入していますか？"
        subText="加入の場合は「加入証明書」をご提出ください"
      >
        <FlexColumn space={0}>
          <RadioControl
            items={[
              { value: 1, label: '加入' },
              { value: 2, label: '未加入' },
            ]}
            control={control}
            name="survey16"
            row
            error={errors.survey16?.message}
          />
          <FlexRow align="center">
            <FileSelectButton
              id="survey16-file"
              accepts={['image/*', 'application/pdf']}
              disabled={Number(survey16) !== 1}
              onSelect={handleSelectSurvey16File}
            >
              加入証を添付
            </FileSelectButton>
            {survey16File && <Label text={survey16File.name} />}
            {!survey16File && survey16FileId && survey16FileName && (
              <IconButton label="ファイルを表示" onClick={() => handleOpenPreview(survey16FileId, survey16FileName)}>
                <AttachmentIcon />
              </IconButton>
            )}
            {!survey16File && survey16FileName && <Label text={survey16FileName} />}
            {errors.survey16File?.message && (
              <Label text={errors.survey16File.message} color="error" variant="caption" />
            )}
          </FlexRow>
        </FlexColumn>
      </FormRow>
      <FormRow
        text="（個人事業主の方に質問）特別加入労災保険に加入していますか？"
        subText="※加入の場合は「加入証明書」をご提出ください"
      >
        <FlexColumn space={0}>
          <RadioControl
            items={[
              { value: 1, label: '加入' },
              { value: 2, label: '未加入' },
            ]}
            control={control}
            name="survey4"
            row
            error={errors.survey4?.message}
          />
          <FlexRow align="center">
            <FileSelectButton
              id="survey4-file"
              accepts={['image/*', 'application/pdf']}
              disabled={Number(survey4) !== 1}
              onSelect={handleSelectSurvey4File}
            >
              加入証を添付
            </FileSelectButton>
            {survey4File && <Label text={survey4File.name} />}
            {!survey4File && survey4FileId && survey4FileName && (
              <IconButton label="ファイルを表示" onClick={() => handleOpenPreview(survey4FileId, survey4FileName)}>
                <AttachmentIcon />
              </IconButton>
            )}
            {!survey4File && survey4FileName && <Label text={survey4FileName} />}
            {errors.survey4File?.message && <Label text={errors.survey4File.message} color="error" variant="caption" />}
          </FlexRow>
        </FlexColumn>
      </FormRow>
      <FormRow text="労災上乗せ補償制度に加入していますか？">
        <RadioControl
          items={[
            { value: 1, label: '加入' },
            { value: 2, label: '未加入' },
          ]}
          control={control}
          name="survey5"
          row
          error={errors.survey5?.message}
        />
      </FormRow>
      <FormRow text="健康保険に加入していますか" subText="加入している場合は健康保険の名称を記入してください">
        <FlexColumn space={0}>
          <RadioControl
            items={[
              { value: 1, label: '加入' },
              { value: 2, label: '未加入' },
            ]}
            control={control}
            name="survey6"
            row
            error={errors.survey6?.message}
          />
          <TextField register={register('survey6Name')} placeholder="協会けんぽ" error={errors.survey6Name?.message} />
        </FlexColumn>
      </FormRow>
      <FormRow text="年金保険に加入していますか" subText="加入している場合は年金保険の名称を記入してください">
        <FlexColumn space={0}>
          <RadioControl
            items={[
              { value: 1, label: '加入' },
              { value: 2, label: '未加入' },
            ]}
            control={control}
            name="survey7"
            row
            error={errors.survey7?.message}
          />
          <TextField register={register('survey7Name')} placeholder="厚生年金" error={errors.survey7Name?.message} />
        </FlexColumn>
      </FormRow>
      <FormRow text="（従業員のいる方に質問）雇用保険に加入していますか？">
        <RadioControl
          items={[
            { value: 1, label: '加入' },
            { value: 2, label: '未加入' },
          ]}
          control={control}
          name="survey8"
          row
          error={errors.survey8?.message}
        />
      </FormRow>
      <FormRow text="賠償責任保険に加入していますか？" subText="※加入の場合は「加入証明書」をご提出ください。">
        <FlexColumn space={0}>
          <RadioControl
            items={[
              { value: 1, label: '加入' },
              { value: 2, label: '未加入' },
            ]}
            control={control}
            name="survey9"
            row
            error={errors.survey9?.message}
          />
          <FlexRow align="center">
            <FileSelectButton
              id="survey9-file"
              accepts={['image/*', 'application/pdf']}
              disabled={Number(survey9) !== 1}
              onSelect={handleSelectSurvey9File}
            >
              加入証を添付
            </FileSelectButton>
            {survey9File && <Label text={survey9File.name} />}
            {!survey9File && survey9FileId && survey9FileName && (
              <IconButton label="ファイルを表示" onClick={() => handleOpenPreview(survey9FileId, survey9FileName)}>
                <AttachmentIcon />
              </IconButton>
            )}
            {!survey9File && survey9FileName && <Label text={survey9FileName} />}
            {errors.survey9File?.message && <Label text={errors.survey9File.message} color="error" variant="caption" />}
          </FlexRow>
        </FlexColumn>
      </FormRow>
      <FormRow text="建設現場で働き始めたのは何年頃ですか">
        <FlexRow wrap align="center">
          <Label text="西暦" />
          <NumberControl control={control} name="survey10" suffix="年" size="xs" error={errors.survey10?.message} />
        </FlexRow>
      </FormRow>
      <FormRow text="従業員および現場作業を行う事業主（代表者・一人親方）は、労働安全衛生法に基づく定期健診に準じた健康診断を毎年受診していますか？">
        <RadioControl
          items={[
            { value: 1, label: 'はい' },
            { value: 2, label: 'いいえ' },
          ]}
          control={control}
          name="survey12"
          row
          error={errors.survey12?.message}
        />
      </FormRow>
      <FormRow text="（従業員のいる方に質問）送り出し教育実施していますか？">
        <RadioControl
          items={[
            { value: 1, label: 'はい' },
            { value: 2, label: 'いいえ' },
          ]}
          control={control}
          name="survey13"
          row
          error={errors.survey13?.message}
        />
      </FormRow>
      <FormRow text="作業に必要な資格・免許を取得していることを（下請けを含む）確認していますか？">
        <RadioControl
          items={[
            { value: 1, label: 'はい' },
            { value: 2, label: 'いいえ' },
          ]}
          control={control}
          name="survey14"
          row
          error={errors.survey14?.message}
        />
      </FormRow>
      <FormRow text="工事を下請けに委託することはありますか？" subText="※下請がいる場合、「下請一覧」をご提出ください">
        <RadioControl
          items={[
            { value: 1, label: 'はい' },
            { value: 2, label: 'いいえ' },
          ]}
          control={control}
          name="survey15"
          row
          error={errors.survey15?.message}
        />
      </FormRow>

      {/* ファイルプレビューダイアログ */}
      <FilePreviewDialog file={previewFile} onClose={handleClosePreview} />
    </FlexColumn>
  )
}
